import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, Sparkles, Wifi, Smartphone, Users, Heart,
  Music, Building2, GraduationCap, Rocket, Star, Shield,
  Zap, Eye, ChevronDown, Mic2, Trophy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from '../components/ui/accordion';
import { useLang } from '../i18n/LanguageContext';
import useSocialLinks from '../hooks/useSocialLinks';
import ColorSimulator from '../components/ColorSimulator';
import HeroVideo from '../components/HeroVideo';
import LangSuggestBanner from '../components/LangSuggestBanner';


const BRACELET_IMG = '/kit-product.png';
const IMMERSION_IMG = 'https://images.unsplash.com/photo-1671432403854-cbe798eb44fb?auto=format&fit=crop&q=80&w=1920';

/* ─── Animated Section Wrapper ─── */
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

/* ─── Step Card ─── */
const StepCard = ({ number, icon: Icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="glass-card rounded-2xl p-8 text-center hover:-translate-y-2 transition-transform duration-300"
      data-testid={`step-card-${number}`}
    >
      <div className="w-14 h-14 rounded-full bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mx-auto mb-6">
        <Icon className="w-6 h-6 text-purple-400" />
      </div>
      <span className="text-sm tracking-widest uppercase text-purple-400 font-body mb-3 block">
        Étape {number}
      </span>
      <h3 className="font-heading text-xl font-bold text-white mb-3">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed font-body">{description}</p>
    </motion.div>
  );
};

/* ─── Use Case Card ─── */
// Chaque occasion renvoie vers les photos correspondantes de la galerie
const USECASE_GALLERY = ['mariage', 'club', 'club', 'festival', 'club', 'corporate', 'corporate', 'corporate', 'concert', 'sport'];

const UseCaseCard = ({ icon: Icon, title, description, delay, galleryCategory, label }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      className="glass-card rounded-2xl p-6 group hover:-translate-y-1 transition-all duration-300"
      data-testid={`usecase-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Link to={`/galerie?categorie=${galleryCategory || 'all'}`} className="block cursor-pointer">
      <div className="w-10 h-10 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center mb-4 group-hover:bg-purple-600/20 transition-colors">
        <Icon className="w-5 h-5 text-purple-400" />
      </div>
      <h4 className="font-heading text-base font-bold text-white mb-2">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed font-body">{description}</p>
      <span className="inline-block mt-3 text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
        {label} →
      </span>
      </Link>
    </motion.div>
  );
};

/* ─── Benefit Item ─── */
const BenefitItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4 items-start">
    <div className="w-10 h-10 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="w-5 h-5 text-purple-400" />
    </div>
    <div>
      <h4 className="font-heading text-base font-bold text-white mb-1">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed font-body">{description}</p>
    </div>
  </div>
);

/* ─────────── MAIN COMPONENT ─────────── */
const Home = () => {
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  const { t } = useLang();
  const social = useSocialLinks();
  const sameAs = Object.values(social).filter(Boolean);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setShowHeroVideo(true), 3000);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/reviews`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setReviews(Array.isArray(d) ? d : []))
      .catch(() => {});
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      <>
        <title>{t.seo.homeTitle}</title>
        <meta name="description" content={t.seo.homeDesc} />
        <link rel="canonical" href="https://www.party-led.com/" />
        <meta property="og:title" content={t.seo.homeTitle} />
        <meta property="og:description" content={t.seo.homeDesc} />
        <meta property="og:image" content="https://www.party-led.com/kit-product.png" />
        <meta property="og:url" content="https://www.party-led.com/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": t.faq.items.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
        <meta property="og:site_name" content="Party LED" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.party-led.com/kit-product.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Party LED',
          url: 'https://www.party-led.com',
          logo: 'https://www.party-led.com/kit-product.png',
          description: "Bracelets LED synchronisés pour événements : mariages, festivals, soirées d'entreprise et clubs.",
          ...(sameAs.length ? { sameAs } : {}),
          contactPoint: [
            { '@type': 'ContactPoint', telephone: '+33-4-91-48-12-13', contactType: 'sales', areaServed: 'EU', availableLanguage: ['French', 'English', 'Italian'] },
            { '@type': 'ContactPoint', telephone: '+971-58-507-0177', contactType: 'sales', areaServed: 'AE', availableLanguage: ['English', 'Arabic'] },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Party LED',
          url: 'https://www.party-led.com',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.party-led.com/catalogue?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        })}</script>
      </>

      <LangSuggestBanner />

      {/* ═══════════ 1. HERO ═══════════ */}
      <section className="relative h-[54vh] md:h-[60vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Fond : vidéo YouTube recadrée (titre et contrôles hors cadre) + photo en secours */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/hero-crowd.jpg`}
            alt="Foule portant des bracelets LED synchronisés lors d'une soirée Party LED"
            className="absolute inset-0 w-full h-full object-cover hero-kenburns"
            data-testid="hero-bg"
          />
          {/* Après 3 s, la vidéo prend le relais (et disparaît si YouTube ne la joue pas) */}
          {showHeroVideo && <HeroVideo />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/75 to-[#050505]/55" />
        </div>
        <style>{`
          @keyframes heroKenburns { 0% { transform: scale(1.04) translate3d(0,0,0) } 100% { transform: scale(1.16) translate3d(-1.5%, -1.5%, 0) } }
          .hero-kenburns { animation: heroKenburns 26s ease-in-out infinite alternate; }
          @keyframes heroVideoIn { from { opacity: 0 } to { opacity: 1 } }
          .hero-video-in { animation: heroVideoIn 1.6s ease-out both; }
          @media (prefers-reduced-motion: reduce) { .hero-kenburns { animation: none } }
        `}</style>

        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-block text-sm tracking-[0.3em] uppercase text-purple-400 font-body mb-6"
          >
            {t.hero.badge}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-8"
          >
            {t.hero.title1}{' '}
            <span className="text-purple-400">{t.hero.titleHighlight}</span>
          </motion.h1>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10"
        >
          <ChevronDown className="w-6 h-6 text-white/40 animate-bounce" />
        </motion.div>
      </section>

      {/* ═══════════ 3. COMMENT ÇA MARCHE ═══════════ */}
      <Section className="pt-6 pb-12 md:pt-8 md:pb-16 px-6 relative overflow-hidden" id="how-it-works">
        {/* Animated background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600/[0.04] rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '6s' }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/[0.03] rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-700/[0.02] rounded-full blur-[150px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '1s' }} />
          {/* Grid pattern */}
          <div className="absolute inset-0 grid-bg opacity-40" />
          {/* Connecting line between steps */}
          <div className="hidden md:block absolute top-[58%] left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
              {t.howItWorks.badge}
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight">
              {t.howItWorks.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StepCard number={1} icon={Users} title={t.howItWorks.step1Title} description={t.howItWorks.step1Desc} delay={0} />
            <StepCard number={2} icon={Wifi} title={t.howItWorks.step2Title} description={t.howItWorks.step2Desc} delay={0.15} />
            <StepCard number={3} icon={Smartphone} title={t.howItWorks.step3Title} description={t.howItWorks.step3Desc} delay={0.3} />
          </div>
        </div>
      </Section>

      {/* ═══════════ SIMULATEUR DE COULEURS ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="simulateur">
        <div className="max-w-6xl mx-auto relative z-10">
          <ColorSimulator />
        </div>
      </Section>

      {/* ═══════════ 3. FONCTIONNALITÉS CLÉS ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="features">
        {/* Subtle purple glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
              {t.features.badge}
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight">
              {t.features.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* LED Synchronisée */}
            <div className="glass-card rounded-3xl p-8 md:p-10 relative overflow-hidden group" data-testid="feature-led">
              <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/10 rounded-full blur-[80px] group-hover:bg-purple-600/20 transition-all duration-500" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mb-6">
                  <Sparkles className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                  {t.features.ledTitle}
                </h3>
                <p className="text-gray-400 leading-relaxed font-body mb-6">
                  {t.features.ledDesc}
                </p>
                <ul className="space-y-3">
                  {t.features.ledItems.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-gray-300 text-sm font-body">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* NFC Interactive */}
            <div className="glass-card rounded-3xl p-8 md:p-10 relative overflow-hidden group" data-testid="feature-nfc">
              <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/10 rounded-full blur-[80px] group-hover:bg-purple-600/20 transition-all duration-500" />
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center mb-6">
                  <Smartphone className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
                  {t.features.nfcTitle}
                </h3>
                <p className="text-gray-400 leading-relaxed font-body mb-6">
                  {t.features.nfcDesc}
                </p>
                <ul className="space-y-3">
                  {t.features.nfcItems.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-gray-300 text-sm font-body">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══════════ 4. CAS D'USAGE ═══════════ */}
      <Section className="py-12 md:py-16 px-6" id="use-cases">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
              {t.useCases.badge}
            </span>
            <h2 className="font-heading font-bold text-white tracking-tight whitespace-nowrap text-[4.6vw] lg:text-5xl">
              {t.useCases.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {t.useCases.items.map((uc, i) => {
              const icons = [Heart, Music, Star, Users, GraduationCap, Building2, Rocket, Zap, Mic2, Trophy];
              return (
                <UseCaseCard
                  key={uc.title}
                  icon={icons[i]}
                  title={uc.title}
                  description={uc.desc}
                  delay={i * 0.05}
                  galleryCategory={USECASE_GALLERY[i]}
                  label={t.gallery.viewPhotos}
                />
              );
            })}
          </div>

          <div className="flex justify-center mt-12">
            <Link to="/catalogue">
              <Button
                data-testid="hero-cta-primary"
                className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-8 py-6 text-base font-bold transition-all duration-300 hover:shadow-[0_0_30px_rgba(147,51,234,0.5)]"
              >
                {t.hero.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* ═══════════ 5. IMMERSION VISUELLE ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="immersion">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden" data-testid="immersion-section">
            <img loading="lazy"
              src={IMMERSION_IMG}
              alt="Expérience immersive LED"
              className="w-full h-[50vh] md:h-[60vh] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
            <div className="absolute inset-0 flex items-end">
              <div className="p-8 md:p-14 max-w-2xl">
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
                  {t.immersion.title}
                </h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed font-body">
                  {t.immersion.desc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══════════ 6. BÉNÉFICES ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="benefits">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
                {t.benefits.badge}
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
                {t.benefits.title}
              </h2>
              <p className="text-gray-400 text-base leading-relaxed font-body mb-10">
                {t.benefits.desc}
              </p>
              <div className="space-y-6">
                {t.benefits.items.map((b, i) => {
                  const icons = [Eye, Zap, Sparkles, Shield];
                  return <BenefitItem key={b.title} icon={icons[i]} title={b.title} description={b.desc} />;
                })}
              </div>
            </div>

            <div className="relative flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-600/10 rounded-3xl blur-[60px]" />
                <img loading="lazy"
                  src={BRACELET_IMG}
                  alt="Bracelets LED Party LED"
                  className="relative z-10 rounded-3xl max-w-sm w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══════════ 8. RÉASSURANCE ═══════════ */}
      <Section className="py-12 md:py-16 px-6" id="trust">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
              {t.trust.badge}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
              {t.trust.title}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {t.trust.stats.map((stat) => (
              <div key={stat.value} className="glass-card rounded-2xl p-6 text-center" data-testid={`stat-${stat.label}`}>
                <div className="font-heading text-3xl md:text-4xl font-bold text-purple-400 mb-1">{stat.value}</div>
                <div className="text-gray-500 text-xs uppercase tracking-wider font-body">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {t.trust.reassurance.map((item) => (
              <div key={item.title} className="glass-card rounded-2xl p-6">
                <h4 className="font-heading text-base font-bold text-white mb-2">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══════════ 8bis. AVIS CLIENTS ═══════════ */}
      {reviews.length > 0 && (
        <Section className="py-12 md:py-16 px-6" id="avis">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
                Ils nous ont fait confiance
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                Ce que disent nos clients
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-reviews">
              {reviews.slice(0, 6).map((r, i) => (
                <div key={r.id || i} className="glass-card rounded-2xl p-6 flex flex-col" data-testid={`home-review-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-4 h-4 ${n <= (r.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
                      ))}
                    </div>
                    {r.featured && <span className="text-[10px] uppercase tracking-wider text-amber-400">★ À la une</span>}
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-body flex-1">« {r.comment} »</p>
                  <div className="mt-4">
                    <p className="text-white text-sm font-semibold">{r.name}</p>
                    {r.location ? <p className="text-purple-400 text-xs">{r.location}</p> : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ═══════════ 9. FAQ ═══════════ */}
      <Section className="py-12 md:py-16 px-6" id="faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-sm tracking-[0.3em] uppercase text-purple-400 font-body block mb-4">
              {t.faq.badge}
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
              {t.faq.title}
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {t.faq.items.map((faq) => (
              <AccordionItem key={faq.q} value={faq.q} className="glass-card rounded-xl border-0 px-6 overflow-hidden" data-testid={`faq-item-${faq.q.substring(0,20)}`}>
                <AccordionTrigger className="text-white font-heading font-bold text-left hover:text-purple-400 hover:no-underline py-5 text-sm md:text-base">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 font-body text-sm leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* ═══════════ 10. CTA FINALE ═══════════ */}
      <Section className="py-12 md:py-16 px-6 relative" id="cta-final">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-600/8 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10" data-testid="final-cta">
          <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight mb-6">
            {t.finalCta.title1} <span className="text-purple-400">{t.finalCta.titleHighlight}</span>{t.finalCta.titleEnd}
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed font-body mb-10 max-w-xl mx-auto">
            {t.finalCta.desc}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/catalogue">
              <Button
                data-testid="final-cta-explore"
                className="bg-purple-600 hover:bg-purple-500 text-white rounded-full px-10 py-6 text-base font-bold transition-all duration-300 hover:shadow-[0_0_30px_rgba(147,51,234,0.5)]"
              >
                {t.finalCta.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
